import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Typography,
  MenuItem,
  Menu,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DataObjectIcon from '@mui/icons-material/DataObject';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ImageIcon from '@mui/icons-material/Image';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudIcon from '@mui/icons-material/Cloud';
import HistoryIcon from '@mui/icons-material/History';
import { styled } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import LanguageIcon from '@mui/icons-material/Language';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from './AuthProvider';
import { useI18n, useTranslation, SUPPORTED_LOCALES, LOCALE_NAMES } from '../i18n';

interface LayoutProps {
  children: React.ReactNode;
  title: string;
}

const FlexBox = styled(Box)({
  display: 'flex',
});

const Sidebar = styled(Box)(({ theme }) => ({
  width: '260px',
  height: '100vh',
  backgroundColor: theme.palette.background.paper,
  padding: '16px',
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
}));

const MainContent = styled(Box)(({ theme }) => ({
  flex: 1,
  height: '100vh',
  overflow: 'auto',
  backgroundColor: theme.palette.background.default,
}));

const Navbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: '72px',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
}));

export function Layout({ children, title }: LayoutProps): JSX.Element {
  const location = useLocation();
  const [orgMenuAnchorEl, setOrgMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const { locale, setLocale } = useI18n();
  const t = useTranslation();

  const handleOrgMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOrgMenuAnchorEl(event.currentTarget);
  };

  const handleOrgMenuClose = () => {
    setOrgMenuAnchorEl(null);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchorEl(null);
  };

  const handleLocaleChange = (newLocale: string) => {
    setLocale(newLocale as any);
    setProfileMenuAnchorEl(null);
  };

  const getInitials = (name: string) => {
    const nameParts = name.split(' ');
    if (nameParts.length > 1) {
      return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const { user, organizations, activeOrganization, activeRole, logout, switchOrganization } = useAuth();
  const isSystemRole = activeOrganization?.id === 1;
  const isAdmin = activeRole === 'admin';
  const isPipeletOrg = activeOrganization?.id === 1;
  const canViewTeamAndBilling = isPipeletOrg || isAdmin;
  
  const [userCredits, setUserCredits] = React.useState<number>(0);

  // Fetch user's credits
  React.useEffect(() => {
    async function fetchUserCredits() {
      try {
        const response = await fetch('/api/v1/credits');
        if (response.ok) {
          const data = await response.json() as { credits: number };
          setUserCredits(data.credits);
        }
      } catch (error) {
        console.error('Failed to fetch user credits:', error);
      }
    }
    fetchUserCredits();
  }, []);

  const handleSwitchOrg = async (orgId: number) => {
    try {
      await switchOrganization(orgId);
    } catch (error) {
      console.error('Failed to switch organization:', error);
    }
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
  };

  return (
    <FlexBox>
      <Sidebar>
        {activeOrganization && (
          <Box sx={{ mb: 3 }}>
            {organizations.length > 1 ? (
              <>
                <Button
                  fullWidth
                  onClick={handleOrgMenuOpen}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    textTransform: 'none',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    backgroundColor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      borderColor: 'primary.light',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {activeOrganization.name}
                    </Typography>
                  </Box>
                  <KeyboardArrowDownIcon sx={{ color: 'text.secondary' }} />
                </Button>
                <Menu
                  anchorEl={orgMenuAnchorEl}
                  open={Boolean(orgMenuAnchorEl)}
                  onClose={handleOrgMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  sx={{ '& .MuiPaper-root': { width: '220px', mt: 1 } }}
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.id} onClick={() => org.id && handleSwitchOrg(org.id)} selected={org.id === activeOrganization.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backgroundColor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {activeOrganization.name}
                </Typography>
              </Box>
            )}
            
            {/* Credits Display */}
            <Box
              sx={{
                mt: 1,
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, fontSize: '0.7rem' }}>
                {t.layout.myCredits || 'My Credits'}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                ${userCredits.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          <List
            disablePadding
            sx={{
              '& .MuiListSubheader-root': {
                textTransform: 'uppercase',
                fontSize: '0.6875rem',
                fontWeight: 500,
                color: 'text.disabled',
                lineHeight: 'initial',
                padding: '16px 8px 4px 8px',
                backgroundColor: 'transparent',
                letterSpacing: '0.08em',
                '&:first-of-type': {
                  paddingTop: '8px',
                },
              },
              '& .MuiListItemButton-root': {
                padding: '4px 8px',
                borderRadius: '6px',
                marginBottom: '1px',
                transition: 'all 0.2s',
                minHeight: '32px',
              },
              '& .MuiListItemIcon-root': {
                minWidth: 'auto',
                marginRight: '10px',
                color: 'text.secondary',
                '& svg': {
                  fontSize: '1.125rem',
                },
              },
              '& .MuiListItemText-primary': {
                fontSize: '0.8125rem',
                fontWeight: 400,
              },
            }}
          >
            <ListSubheader>{t.layout.home}</ListSubheader>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/wan22-video" selected={location.pathname === '/wan22-video'}>
                <ListItemIcon>
                  <VideoLibraryIcon />
                </ListItemIcon>
                <ListItemText primary={t.layout.wan22Video} />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton component={Link} to="/image-edit" selected={location.pathname === '/image-edit'}>
                <ListItemIcon>
                  <ImageIcon />
                </ListItemIcon>
                <ListItemText primary={t.layout.imageEdit} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/face-swap" selected={location.pathname === '/face-swap'}>
                <ListItemIcon>
                  <ImageIcon />
                </ListItemIcon>
                <ListItemText primary={t.layout.faceSwap} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/text-to-image" selected={location.pathname === '/text-to-image'}>
                <ListItemIcon>
                  <AutoAwesomeIcon />
                </ListItemIcon>
                <ListItemText primary={t.layout.textToImage} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/next-scene" selected={location.pathname === '/next-scene'}>
                <ListItemIcon>
                  <ImageIcon />
                </ListItemIcon>
                <ListItemText primary={t.layout.nextScene} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/ltx-video" selected={location.pathname === '/ltx-video'}>
                <ListItemIcon>
                  <VideoLibraryIcon />
                </ListItemIcon>
                <ListItemText primary={t.layout.ltxVideo} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/video-add-sound" selected={location.pathname === '/video-add-sound'}>
                <ListItemIcon>
                  <VideoLibraryIcon />
                </ListItemIcon>
                <ListItemText primary={t.layout.videoAddSound} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/multi-angle" selected={location.pathname === '/multi-angle'}>
                <ListItemIcon>
                  <ViewInArIcon />
                </ListItemIcon>
                <ListItemText primary={t.layout.multiAngle} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/human-portrait" selected={location.pathname === '/human-portrait'}>
                <ListItemIcon>
                  <AutoAwesomeIcon />
                </ListItemIcon>
                <ListItemText primary={t.layout.humanPortrait} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/models" selected={location.pathname === '/models'}>
                <ListItemIcon>
                  <DataObjectIcon />
                </ListItemIcon>
                <ListItemText primary={t.layout.models} />
              </ListItemButton>
            </ListItem>

            <ListSubheader>{t.layout.history}</ListSubheader>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/history" selected={location.pathname === '/history'}>
                <ListItemIcon>
                  <HistoryIcon />
                </ListItemIcon>
                <ListItemText primary={t.layout.generationHistory} />
              </ListItemButton>
            </ListItem>

            <ListSubheader>{t.layout.organization}</ListSubheader>
            {canViewTeamAndBilling && (
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/team" selected={location.pathname === '/team'}>
                  <ListItemIcon>
                    <GroupIcon />
                  </ListItemIcon>
                  <ListItemText primary={t.layout.team} />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/tokens" selected={location.pathname === '/tokens'}>
                <ListItemIcon>
                  <VpnKeyIcon />
                </ListItemIcon>
                <ListItemText primary={t.layout.apiTokens} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/usage" selected={location.pathname === '/usage'}>
                <ListItemIcon>
                  <ShowChartIcon />
                </ListItemIcon>
                <ListItemText primary={t.layout.usage} />
              </ListItemButton>
            </ListItem>
            {canViewTeamAndBilling && (
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/billing" selected={location.pathname === '/billing'}>
                  <ListItemIcon>
                    <ReceiptIcon />
                  </ListItemIcon>
                  <ListItemText primary={t.layout.billing} />
                </ListItemButton>
              </ListItem>
            )}

            {isSystemRole && (
              <>
                <ListSubheader>{t.layout.system}</ListSubheader>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/model-editor" selected={location.pathname === '/model-editor'}>
                    <ListItemIcon>
                      <EditIcon />
                    </ListItemIcon>
                    <ListItemText primary={t.layout.modelEditor} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/provider-editor" selected={location.pathname === '/provider-editor'}>
                    <ListItemIcon>
                      <CloudIcon />
                    </ListItemIcon>
                    <ListItemText primary={t.layout.providerEditor} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/org-editor" selected={location.pathname === '/org-editor'}>
                    <ListItemIcon>
                      <GroupIcon />
                    </ListItemIcon>
                    <ListItemText primary={t.layout.orgEditor} />
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Sidebar>
      <MainContent>
        <Navbar>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {title}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleProfileMenuOpen}
            sx={{
              borderRadius: '50%',
              minWidth: 'auto',
              p: 0.5,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: '0.875rem',
                bgcolor: 'primary.main',
                fontWeight: 600,
              }}
            >
              {user?.name ? getInitials(user.name) : '?'}
            </Avatar>
          </Button>
          <Menu
            anchorEl={profileMenuAnchorEl}
            open={Boolean(profileMenuAnchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
              },
            }}
          >
            <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LanguageIcon sx={{ fontSize: 14 }} />
                Language
              </Typography>
            </Box>
            {SUPPORTED_LOCALES.map((loc) => (
              <MenuItem
                key={loc}
                onClick={() => handleLocaleChange(loc)}
                selected={loc === locale}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography>{LOCALE_NAMES[loc]}</Typography>
                {loc === locale && <CheckIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
              </MenuItem>
            ))}
            <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 1 }} />
            <MenuItem onClick={handleLogout}>{t.common.logout}</MenuItem>
          </Menu>
        </Navbar>
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: '3200px', margin: '0 auto', width: '100%' }}>{children}</Box>
      </MainContent>
    </FlexBox>
  );
}
